import { CONFIG } from "../config";
import { noteTailGapSeconds } from "../note-gap";
import type { Chord, HandFilter, KeyState, NoteEvent, Song } from "../types";
import { groupChords } from "./chords";
import { assignFingers } from "./fingering";
import { assignHands, DEFAULT_HAND_SPLIT, type HandSplitOptions } from "./hand-split";
import { buildKeyStates, type KeyHighlightCfg } from "./key-highlight";
import { type Judgement, type PlayMode, Scorer, type ScoreStats } from "./scoring";
import { WaitTiming, type WaitTimingStats } from "./wait-timing";

/**
 * Engine / scheduler. Owns the playhead, hand filtering, and
 * tempo scaling, plus two play modes: **wait** (gate at each chord until played)
 * and **scored** (notes flow past the hit line and presses are judged
 * against a timing window). It holds no clock of its own — `update` is driven by
 * the RAF loop's monotonic delta.
 */
export class Engine {
  playhead = 0;
  tempo = 1;
  mode: PlayMode = "wait";
  playing = false;
  hand: HandFilter = "both";
  /** Active A–B practice loop bounds in seconds, or null when unset. */
  loopStart: number | null = null;
  loopEnd: number | null = null;
  /** Coach-driven active region: advance is clamped at `windowEnd` (no wrap). */
  windowStart: number | null = null;
  windowEnd: number | null = null;
  /** Whole-song pitch shift in semitones (play in an easier key). */
  transpose = 0;

  readonly duration: number;
  readonly song: Song;
  private readonly allNotes: NoteEvent[];
  private filtered: NoteEvent[] = [];
  private fingers = new Map<NoteEvent, number>();
  private chords: Chord[] = [];
  private index = 0;
  /** Longest note duration (s) in `filtered` — bounds the active-note scan in keyStates. */
  private maxNoteDur = 0;
  private scorer = new Scorer([]);
  private prevHeld = new Set<number>();
  /** Playhead time (s) each currently-held pitch was pressed, for held-duration. */
  private pressedAt = new Map<number, number>();
  private completed = false;
  private mistakeBaseline = 0;
  /** Wait-mode timing tally (early/on-time/late vs each note's keyline arrival). */
  private waitTiming = new WaitTiming();
  /** Per-gate wait state: playhead when the chord first became (continuously) held — set
   *  while it's still falling, so an early hold is measurable; and the seconds the playhead
   *  stayed pinned at the keyline waiting for the player (lateness). */
  private gateFirstHeld: number | null = null;
  private gatePinnedWait = 0;
  /** A wait gate completed this frame — read (and cleared) via takeGateCompleted(). */
  private gateCompleted = false;
  /** Notes before this time are excluded from scoring (windowed rep / loop lead-in). */
  private gateFrom = 0;
  private handSplit: HandSplitOptions = { ...DEFAULT_HAND_SPLIT };

  constructor(song: Song) {
    this.allNotes = song.notes;
    this.duration = song.duration;
    this.song = song;
    // Start a lookahead-window before the first onset so the opening notes
    // enter from the top of the screen and fall toward the keys (the lead-in)
    // rather than starting pinned at the hit line.
    this.playhead = -CONFIG.lookaheadSeconds;
    assignHands(this.allNotes, this.handSplit);
    this.rebuild();
  }

  /** Hand-filtered notes for rendering the highway. */
  get notes(): readonly NoteEvent[] {
    return this.filtered;
  }

  /** Ergonomic finger number (1–5) for a rendered note, or undefined (display-only). */
  fingerOf(note: NoteEvent): number | undefined {
    return this.fingers.get(note);
  }

  /** Fraction of the song completed, 0–1 (for the transport readout). */
  get progress(): number {
    return this.duration > 0 ? Math.min(1, Math.max(0, this.playhead / this.duration)) : 0;
  }

  /** Live tally for the current scored run. */
  get scoreStats(): ScoreStats {
    return this.scorer.stats;
  }

  /** Wait-mode timing tally (early/on-time/late) for the current run. */
  get waitTimingStats(): WaitTimingStats {
    return this.waitTiming.stats;
  }

  /** Relative dynamics + articulation (0..1 each) over the current run's hits. */
  expressionScores(): { dynamics: number; articulation: number } {
    return this.scorer.expressionScores();
  }

  setHand(hand: HandFilter): void {
    this.hand = hand;
    this.rebuild();
  }

  /** Re-run the hand split with new override options (live, no re-parse). */
  setHandOverride(opts: HandSplitOptions): void {
    this.handSplit = opts;
    assignHands(this.allNotes, this.handSplit);
    this.rebuild();
  }

  /** Shift the whole song by `semitones` (live; the player plays the new key). */
  setTranspose(semitones: number): void {
    this.transpose = semitones;
    this.rebuild();
  }

  /** Count of assigned hands across all notes (for tests / debugging). */
  handCounts(): { left: number; right: number; unknown: number } {
    const counts = { left: 0, right: 0, unknown: 0 };
    for (const n of this.allNotes) counts[n.hand] += 1;
    return counts;
  }

  /** Switch play mode; resets the playhead and any in-progress score. */
  setMode(mode: PlayMode): void {
    this.mode = mode;
    this.restart();
  }

  restart(): void {
    this.playhead = -CONFIG.lookaheadSeconds;
    this.index = 0;
    this.playing = false;
    this.completed = false;
    this.prevHeld.clear();
    this.pressedAt.clear();
    this.scorer.reset();
    this.waitTiming.reset();
    this.gateFirstHeld = null;
    this.gatePinnedWait = 0;
    this.gateCompleted = false;
    this.mistakeBaseline = 0;
    this.gateFrom = 0;
  }

  /** Arm an A–B practice loop over [start, end) seconds. Ignored if end ≤ start. */
  setLoop(start: number, end: number): void {
    if (end <= start) return;
    this.loopStart = start;
    this.loopEnd = end;
  }

  clearLoop(): void {
    this.loopStart = null;
    this.loopEnd = null;
  }

  /** Bound advance to [start, end) — the playhead clamps at `end` (no auto-wrap).
   *  The Coach drives looping/cadence by reading the playhead and re-seeking. */
  setWindow(start: number, end: number): void {
    this.windowStart = start;
    this.windowEnd = end;
  }

  clearWindow(): void {
    this.windowStart = null;
    this.windowEnd = null;
  }

  /** New miss+wrong judgements since the last call (the Coach's per-frame signal),
   *  robust to scorer resets (a reset drops the running total back toward 0). */
  takeMistakes(): number {
    const current = this.scorer.stats.misses + this.scorer.stats.wrong;
    const delta = current >= this.mistakeBaseline ? current - this.mistakeBaseline : current;
    this.mistakeBaseline = current;
    return delta;
  }

  /** True once per completed wait gate, cleared by the read (like `takeMistakes()`). The
   *  frame a gate completes the playhead is still pinned at the note's onset and has not
   *  advanced — so a caller can hold it before it moves. `main.ts` uses this to drop the
   *  song back in on a metronome beat. */
  takeGateCompleted(): boolean {
    const done = this.gateCompleted;
    this.gateCompleted = false;
    return done;
  }

  /** Hits within `thresholdSec` of their onset (the Coach's timing gate). */
  onTimeHits(thresholdSec: number): number {
    return this.scorer.onTimeHits(thresholdSec);
  }

  /**
   * Reposition the playhead to `seconds` (with the lead-in already applied by the
   * caller), re-pointing wait-mode gating to the first chord at/after `gateFrom`
   * and resetting the scorer so a looped region re-judges. Shared by the A–B loop
   * and future drivers (the Coach).
   */
  seekTo(seconds: number, gateFrom = seconds): void {
    this.playhead = seconds;
    this.gateFrom = gateFrom;
    this.index = this.chords.findIndex((c) => c.onset >= gateFrom);
    if (this.index < 0) this.index = this.chords.length;
    this.scorer.reset();
    // Don't score the lead-in notes before the gate point (windowed rep / loop).
    if (gateFrom > 0) this.scorer.skipBefore(gateFrom);
    this.prevHeld.clear();
    this.pressedAt.clear();
    this.mistakeBaseline = 0;
    // Clear the in-flight wait-mode gate state too, or a partial early-hold/pinned-wait
    // left mid-gate at the loop end leaks into the first gate of the next pass and tallies
    // a bogus early/late in the timing HUD.
    this.gateFirstHeld = null;
    this.gatePinnedWait = 0;
  }

  /** Judgement for a note in scored mode (for rendering), or null if pending. */
  judgementFor(note: NoteEvent): Judgement | null {
    return this.scorer.judgementFor(note);
  }

  /** Recent wrong presses for the staff overlay (non-consuming). */
  recentWrong(): readonly { pitch: number; time: number }[] {
    return this.scorer.recentWrong();
  }

  /** True exactly once after a scored run reaches the end (for saving stats). */
  consumeCompletion(): boolean {
    if (!this.completed) return false;
    this.completed = false;
    return true;
  }

  /**
   * Advance the simulation. Wait-mode holds the playhead at the next expected
   * chord until every pitch is held; scored mode advances freely and
   * judges presses against the timing window.
   */
  update(
    dtSeconds: number,
    held: ReadonlySet<number>,
    heldVelocity?: ReadonlyMap<number, number>,
  ): void {
    if (!this.playing) return;
    if (this.mode === "scored") this.advanceScored(dtSeconds, held, heldVelocity);
    else if (this.mode === "auto") this.advanceAuto(dtSeconds);
    else this.advanceWait(dtSeconds, held);

    // A–B loop: once past B, wrap back to A with the lead-in so the first looped
    // chord falls in (gating resumes at A, not at the lead-in position).
    if (this.loopStart !== null && this.loopEnd !== null && this.playhead >= this.loopEnd) {
      this.seekTo(this.loopStart - CONFIG.lookaheadSeconds, this.loopStart);
    }

    // Coach active region: hold the playhead at the window end (no wrap).
    if (this.windowEnd !== null && this.playhead > this.windowEnd) {
      this.playhead = this.windowEnd;
    }
  }

  /**
   * Per-key feedback for the current frame: expected pitches not yet held,
   * expected pitches held (correct), and held pitches that are not expected
   * (wrong). Drives the keyboard coloring.
   */
  /** Per-key display state for this frame — a duration-honoring state machine over the
   *  notes whose span touches the playhead (key-highlight.ts). Demo/auto just lights the
   *  sounding (held) pitches green; there's no player to judge. */
  keyStates(held: ReadonlySet<number>): Map<number, KeyState> {
    if (this.mode === "auto") {
      const states = new Map<number, KeyState>();
      for (const pitch of held) states.set(pitch, "correct");
      return states;
    }
    const cfg: KeyHighlightCfg = {
      leadSec: CONFIG.keyLeadMs / 1000,
      windowSec: CONFIG.hitWindowMs / 1000,
      releaseMarginSec: CONFIG.keyReleaseMarginMs / 1000,
    };
    return buildKeyStates(
      this.activeKeyNotes(this.playhead, cfg.leadSec),
      this.playhead,
      held,
      cfg,
    );
  }

  /** Notes whose `[onset−lead, onset+duration)` spans `t`, within the active drill window —
   *  the candidates for key highlighting. Bounded scan over the onset-sorted notes. */
  private activeKeyNotes(t: number, leadSec: number): NoteEvent[] {
    const hi = t + leadSec;
    const lo = t - this.maxNoteDur;
    const notes = this.filtered;
    const ws = this.windowStart;
    const we = this.windowEnd;
    let a = 0;
    let b = notes.length;
    while (a < b) {
      const m = (a + b) >> 1;
      if ((notes[m]?.onset ?? 0) < lo) a = m + 1;
      else b = m;
    }
    const out: NoteEvent[] = [];
    for (let i = a; i < notes.length; i++) {
      const n = notes[i];
      if (!n || n.onset > hi) break;
      if (t >= n.onset + n.duration) continue; // span already past
      if (ws !== null && we !== null && (n.onset < ws || n.onset >= we)) continue; // outside drill window
      out.push(n);
    }
    return out;
  }

  private advanceWait(dtSeconds: number, held: ReadonlySet<number>): void {
    const current = this.chords[this.index];
    // Wrong-note accuracy: tally each newly-pressed pitch the current gate doesn't expect.
    if (current) {
      for (const pitch of held) {
        if (!this.prevHeld.has(pitch) && !current.pitches.includes(pitch)) {
          this.waitTiming.recordWrong();
        }
      }
    }
    this.prevHeld.clear();
    for (const pitch of held) this.prevHeld.add(pitch);

    if (!current) {
      this.playhead += dtSeconds * this.tempo;
      if (this.playhead >= this.duration) this.playing = false;
      return;
    }
    const allHeld = current.pitches.every((p) => held.has(p));
    const next = this.playhead + dtSeconds * this.tempo;
    if (next >= current.onset) {
      this.playhead = current.onset; // pin at the keyline (the note's scheduled arrival)
      if (allHeld) {
        // Gate completed — judge timing. Held early (continuously, while falling) →
        // negative (how far before arrival, in real seconds via tempo); held only after
        // the playhead pinned → positive (the seconds the note waited for the player).
        const error =
          this.gateFirstHeld !== null && this.gateFirstHeld < current.onset
            ? (this.gateFirstHeld - current.onset) / this.tempo
            : this.gatePinnedWait;
        this.waitTiming.record(error);
        this.gateCompleted = true;
        this.index++;
        this.gateFirstHeld = null;
        this.gatePinnedWait = 0;
      } else {
        this.gatePinnedWait += dtSeconds; // pinned at the line, waiting for the player
      }
      return;
    }
    // Still falling toward the line: note when the chord first becomes (and stays) held,
    // so an early hold is measurable; a release before arrival clears it.
    if (allHeld) {
      if (this.gateFirstHeld === null) this.gateFirstHeld = this.playhead;
    } else {
      this.gateFirstHeld = null;
    }
    this.playhead = next;
  }

  /** Demo/attract advance: flow the playhead at tempo, judge nothing, write no
   *  score. Completion still fires so the demo reel can pick the next piece. */
  private advanceAuto(dtSeconds: number): void {
    this.playhead += dtSeconds * this.tempo;
    if (this.playhead >= this.duration + CONFIG.hitWindowMs / 1000) {
      this.playing = false;
      this.completed = true;
    }
  }

  /** Notes from `notes` sounding at the current playhead. The tail is released a hair
   *  early (display-only gap) so a repeated note re-presses instead of holding — shared
   *  by the demo key-light and MIDI-out so they agree frame-for-frame. */
  private soundingFrom(notes: readonly NoteEvent[]): NoteEvent[] {
    const out: NoteEvent[] = [];
    for (const n of notes) {
      const end = n.onset + n.duration - noteTailGapSeconds(n.duration);
      if (n.onset <= this.playhead && this.playhead < end) out.push(n);
    }
    return out;
  }

  /** Pitches currently sounding in the displayed (hand-filtered, transposed) set — the
   *  demo feeds these as the "held" set so on-screen keys light without real input. */
  soundingPitches(): Set<number> {
    return new Set(this.soundingFrom(this.filtered).map((n) => n.midi));
  }

  /** Notes sounding that the player is **not** responsible for — sent to the keyboard's
   *  own synth (F1). `owned` is the hand the player covers: `"none"` → the whole song
   *  sounds out ("play it for me"); a hand → the *other* hand accompanies. Unknown-hand
   *  notes count as owned (the player plays them on screen — don't double them). The
   *  song's transpose is applied so accompaniment stays in the player's key. */
  soundingOut(owned: HandFilter | "none"): { midi: number; velocity: number }[] {
    const owns = (n: NoteEvent): boolean => {
      if (owned === "none") return false;
      if (n.hand === "unknown") return true;
      return owned === "both" || n.hand === owned;
    };
    return this.soundingFrom(this.allNotes.filter((n) => !owns(n))).map((n) => ({
      midi: n.midi + this.transpose,
      velocity: n.velocity,
    }));
  }

  private advanceScored(
    dtSeconds: number,
    held: ReadonlySet<number>,
    heldVelocity?: ReadonlyMap<number, number>,
  ): void {
    this.playhead += dtSeconds * this.tempo;
    // Clamp at the coach window BEFORE judging, so presses/misses aren't scored a
    // frame past the active drill region (which would feed the coach's gates).
    if (this.windowEnd !== null && this.playhead > this.windowEnd) this.playhead = this.windowEnd;
    for (const pitch of held) {
      if (!this.prevHeld.has(pitch)) {
        this.scorer.judgePress(pitch, this.playhead, heldVelocity?.get(pitch) ?? 1);
        this.pressedAt.set(pitch, this.playhead);
      }
    }
    // Releases: a pitch held last frame but not now — record how long it was held,
    // in playhead seconds (the single clock, not a MIDI timestamp), for articulation.
    for (const pitch of this.prevHeld) {
      if (!held.has(pitch)) {
        const at = this.pressedAt.get(pitch);
        if (at !== undefined) this.scorer.recordHold(pitch, this.playhead - at);
        this.pressedAt.delete(pitch);
      }
    }
    this.prevHeld.clear();
    for (const pitch of held) this.prevHeld.add(pitch);
    this.scorer.sweepMisses(this.playhead);

    if (this.playhead >= this.duration + CONFIG.hitWindowMs / 1000) {
      this.playing = false;
      this.completed = true;
    }
  }

  private rebuild(): void {
    const matched = this.allNotes.filter((n) => this.matchesHand(n));
    // Apply the transpose as a copy so the source note model is never mutated
    // (hand assignment mutates `allNotes` in place; pitch must not).
    const transposed =
      this.transpose === 0
        ? matched
        : matched.map((n) => ({ ...n, midi: n.midi + this.transpose }));
    this.filtered = dedupeUnisons(transposed);
    this.chords = groupChords(this.filtered);
    this.maxNoteDur = this.filtered.reduce((m, n) => Math.max(m, n.duration), 0);
    this.fingers = assignFingers(this.filtered);
    this.index = this.chords.findIndex((c) => c.onset >= this.playhead);
    if (this.index < 0) this.index = this.chords.length;
    this.scorer.load(this.filtered);
    // load() resets the scorer — re-apply the active gate so a live hand/transpose
    // change mid-run doesn't start scoring the windowed-rep lead-in notes again.
    if (this.gateFrom > 0) this.scorer.skipBefore(this.gateFrom);
    this.prevHeld.clear();
    this.pressedAt.clear();
  }

  private matchesHand(n: NoteEvent): boolean {
    if (this.hand === "both" || n.hand === "unknown") return true;
    return n.hand === this.hand;
  }
}

/** Collapse exact unisons — notes sharing a pitch AND onset — to a single note,
 *  keeping the longest. A key can't be pressed twice at once, so two identical notes
 *  (e.g. both hands converging on middle C in a contrary-motion drill) would leave a
 *  bar permanently un-hittable: the gate/scorer can never satisfy the second copy, so
 *  it never plays clean, never masters, and the Coach stalls. Input stays onset-sorted
 *  (Map keeps first-seen order; the source notes are already sorted). */
function dedupeUnisons(notes: readonly NoteEvent[]): NoteEvent[] {
  const byKey = new Map<string, NoteEvent>();
  for (const n of notes) {
    const k = `${n.midi}:${n.onset}`;
    const prev = byKey.get(k);
    if (!prev || n.duration > prev.duration) byKey.set(k, n);
  }
  return [...byKey.values()];
}
